import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const INSPIRE_DURATION = 4;
const EXPIRE_DURATION = 8;

export default function CoherenceCardiaquePage() {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState('inspire');
  const [timer, setTimer] = useState(0);
  const [cycles, setCycles] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    let elapsed = 0;
    let currentPhase = 'inspire';
    setPhase('inspire');
    setTimer(INSPIRE_DURATION);

    intervalRef.current = setInterval(() => {
      elapsed += 0.05;
      if (currentPhase === 'inspire') {
        const remaining = INSPIRE_DURATION - (elapsed % (INSPIRE_DURATION + EXPIRE_DURATION));
        if (remaining <= 0) {
          currentPhase = 'expire';
          setPhase('expire');
          elapsed = 0;
        } else {
          setTimer(Math.ceil(remaining));
        }
      } else {
        const remaining = EXPIRE_DURATION - elapsed;
        if (remaining <= 0) {
          currentPhase = 'inspire';
          setPhase('inspire');
          elapsed = 0;
          setCycles(c => c + 1);
        } else {
          setTimer(Math.ceil(remaining));
        }
      }
    }, 50);

    return () => clearInterval(intervalRef.current);
  }, [active]);

  const stop = () => { setActive(false); clearInterval(intervalRef.current); };
  const reset = () => { stop(); setCycles(0); setTimer(0); setPhase('inspire'); };

  const scale = active
    ? phase === 'inspire'
      ? 1 + ((INSPIRE_DURATION - timer) / INSPIRE_DURATION) * 0.5
      : 1.5 - ((EXPIRE_DURATION - timer) / EXPIRE_DURATION) * 0.5
    : 1;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-lg mx-auto px-4 py-8 sm:py-12 text-center">
        <Link to="/dashboard" className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-8 justify-center transition-colors duration-200"><ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour</Link>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 className="text-3xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>Cohérence cardiaque</h1>
          <p className="text-[#6A7771] mb-10">4 secondes d'inspiration / 8 secondes d'expiration</p>
        </motion.div>

        <div className="flex items-center justify-center mb-10" style={{ height: 280 }}>
          <motion.div
            animate={{
              scale: scale,
              backgroundColor: phase === 'inspire' ? '#6B8576' : '#D9A892',
            }}
            transition={{ duration: 0.15, ease: "linear" }}
            className="rounded-full flex items-center justify-center"
            style={{
              width: 160,
              height: 160,
              opacity: active ? 0.9 : 0.25,
              boxShadow: active ? `0 0 60px ${phase === 'inspire' ? 'rgba(107,133,118,0.3)' : 'rgba(217,168,146,0.3)'}` : 'none'
            }}
            data-testid="breathing-ball"
          >
            <AnimatePresence mode="wait">
              {active && (
                <motion.div
                  key={phase}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-white text-center"
                >
                  <p className="text-3xl font-light" style={{ fontFamily: 'Outfit' }}>{timer}</p>
                  <p className="text-xs uppercase tracking-[0.15em] mt-1 opacity-80">{phase === 'inspire' ? 'Inspirez' : 'Expirez'}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {active && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-[#6A7771] mb-6">
            {cycles} cycle{cycles !== 1 ? 's' : ''} complete{cycles !== 1 ? 's' : ''}
          </motion.p>
        )}

        <div className="flex gap-3 justify-center">
          {!active ? (
            <button onClick={() => setActive(true)} className="px-10 py-3.5 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 text-sm" data-testid="start-breathing">
              Commencer
            </button>
          ) : (
            <>
              <button onClick={stop} className="px-6 py-3 bg-white border border-[#EBE7E0] text-[#2C3531] rounded-full font-medium hover:bg-[#FAF9F6] transition-all duration-300 text-sm" data-testid="stop-breathing">Pause</button>
              <button onClick={reset} className="px-6 py-3 bg-[#D9A892] text-white rounded-full font-medium hover:bg-[#C2937E] transition-all duration-300 text-sm" data-testid="reset-breathing">Arreter</button>
            </>
          )}
        </div>

        <div className="mt-14 bg-white rounded-3xl border border-[#EBE7E0] p-6 text-left">
          <h3 className="font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }}>En cas d'urgence</h3>
          <p className="text-sm text-[#6A7771] leading-relaxed">
            Concentrez-vous sur la boule. Inspirez quand elle grossit (4s), expirez quand elle rétrécit (8s).
            Répétez pendant 3 à 5 minutes pour calmer votre système nerveux.
          </p>
        </div>
      </main>
    </div>
  );
}
